import React, { useMemo, useState } from 'react';
import { useQuery, useMutation } from '@apollo/client';
import { useSelector } from 'react-redux';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  useTheme,
} from '@mui/material';
import {
  AccountBox as AccountBoxIcon,
  Add as AddIcon,
  BarChart as BarChartIcon,
  Delete as DeleteIcon,
  Download as DownloadIcon,
  Edit as EditIcon,
  EditNote as EditNoteIcon,
  FormatListBulleted as FormatListBulletedIcon,
  Password as PasswordIcon,
  PeopleAlt as PeopleAltIcon,
  PersonAdd as PersonAddIcon,
  Rule as RuleIcon,
  Upload as UploadIcon,
} from '@mui/icons-material';
import { RootState } from '../../store';
import { LIST_PROFILES } from '../../graphql/queries';
import { CREATE_PROFILE, DELETE_PROFILE, UPDATE_PROFILE } from '../../graphql/mutations';
import { Profile, ProfilePermissions } from '../../types/models';
import { canAccess } from '../../utils/permissions';
import ProfileFormDialog, { ProfileFormValues } from './components/ProfileFormDialog';

const formatDate = (d?: string) => (d ? new Date(d).toLocaleString('ja-JP') : '-');

const stripTypename = <T,>(obj: T): T => {
  if (Array.isArray(obj)) return obj.map(stripTypename) as unknown as T;
  if (obj && typeof obj === 'object') {
    const { __typename, ...rest } = obj as any;
    return Object.fromEntries(Object.entries(rest).map(([k, v]) => [k, stripTypename(v)])) as T;
  }
  return obj;
};

const PermIcon: React.FC<{
  icon: React.ReactElement;
  tooltip: string;
  bgcolor: string;
}> = ({ icon, tooltip, bgcolor }) => (
  <Tooltip title={tooltip} placement="top" arrow>
    <Box
      component="span"
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: 22,
        height: 22,
        borderRadius: '4px',
        bgcolor,
        color: 'white',
        flexShrink: 0,
        '& svg': { fontSize: '13px' },
      }}
    >
      {icon}
    </Box>
  </Tooltip>
);

type PermIconDef = { icon: React.ReactElement; tooltip: string; bgcolor: string };

const PermissionSummary: React.FC<{ perms: ProfilePermissions }> = ({ perms }) => {
  const sections: { label: string; show: boolean; viewIcons: PermIconDef[]; editIcons: PermIconDef[] }[] = [
    {
      label: 'ユーザー情報',
      show: perms.userMgmt.view.canView,
      viewIcons: [
        { icon: <PeopleAltIcon />, tooltip: 'ユーザー一覧', bgcolor: '#5D84AF' },
        ...(perms.userMgmt.view.canCsvDownload ? [{ icon: <DownloadIcon />, tooltip: 'CSVダウンロード', bgcolor: '#5EB600' }] : []),
      ],
      editIcons: [
        ...(perms.userMgmt.edit.canCreateNew ? [{ icon: <PersonAddIcon />, tooltip: '新規登録', bgcolor: '#5EB600' }] : []),
        ...(perms.userMgmt.edit.canCsvImport ? [{ icon: <UploadIcon />, tooltip: 'CSVインポート', bgcolor: '#FF9800' }] : []),
        ...(perms.userMgmt.edit.canBulkUpdate ? [{ icon: <EditNoteIcon />, tooltip: '一括更新', bgcolor: '#5D84AF' }] : []),
        ...(perms.userMgmt.edit.canUpdate ? [{ icon: <EditIcon />, tooltip: '個別編集', bgcolor: '#5D84AF' }] : []),
        ...(perms.userMgmt.edit.canDelete ? [{ icon: <DeleteIcon />, tooltip: '削除', bgcolor: '#F44336' }] : []),
        ...(perms.userMgmt.edit.canChangePassword ? [{ icon: <PasswordIcon />, tooltip: 'パスワード変更', bgcolor: '#FF9800' }] : []),
      ],
    },
    {
      label: 'ロープレ設定',
      show: perms.scenarioMgmt.view.canView,
      viewIcons: [
        { icon: <FormatListBulletedIcon />, tooltip: 'ロープレ一覧', bgcolor: '#5D84AF' },
        ...(perms.scenarioMgmt.view.canCsvDownload ? [{ icon: <DownloadIcon />, tooltip: 'CSVダウンロード', bgcolor: '#5EB600' }] : []),
      ],
      editIcons: [
        ...(perms.scenarioMgmt.edit.canCreateNew ? [{ icon: <AddIcon />, tooltip: '新規登録', bgcolor: '#5EB600' }] : []),
        ...(perms.scenarioMgmt.edit.canCsvImport ? [{ icon: <UploadIcon />, tooltip: 'CSVインポート', bgcolor: '#FF9800' }] : []),
        ...(perms.scenarioMgmt.edit.canUpdate ? [{ icon: <EditIcon />, tooltip: '個別編集', bgcolor: '#5D84AF' }] : []),
        ...(perms.scenarioMgmt.edit.canDelete ? [{ icon: <DeleteIcon />, tooltip: '削除', bgcolor: '#F44336' }] : []),
        ...(perms.scenarioMgmt.edit.canCustomScenarioUpdate ? [{ icon: <RuleIcon />, tooltip: 'カスタム評価', bgcolor: '#FF9800' }] : []),
      ],
    },
    {
      label: '利用分析',
      show: perms.analytics.view.canView,
      viewIcons: [
        { icon: <BarChartIcon />, tooltip: '分析一覧', bgcolor: '#5D84AF' },
        ...(perms.analytics.view.canCsvDownload ? [{ icon: <DownloadIcon />, tooltip: 'CSVダウンロード', bgcolor: '#5EB600' }] : []),
      ],
      editIcons: [],
    },
    {
      label: '権限プロファイル',
      show: perms.profileMgmt.view.canView,
      viewIcons: [
        { icon: <AccountBoxIcon />, tooltip: 'プロファイル一覧', bgcolor: '#5D84AF' },
      ],
      editIcons: [
        ...(perms.profileMgmt.edit.canCreateNew ? [{ icon: <AddIcon />, tooltip: '新規登録', bgcolor: '#5EB600' }] : []),
        ...(perms.profileMgmt.edit.canUpdate ? [{ icon: <EditIcon />, tooltip: '個別編集', bgcolor: '#5D84AF' }] : []),
        ...(perms.profileMgmt.edit.canDelete ? [{ icon: <DeleteIcon />, tooltip: '削除', bgcolor: '#F44336' }] : []),
      ],
    },
  ];

  const activeSections = sections.filter(s => s.show);
  if (activeSections.length === 0) {
    return <Box component="span" sx={{ color: '#999', fontSize: '13px' }}>（全OFF）</Box>;
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
      {activeSections.map(section => (
        <Box key={section.label} sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'nowrap' }}>
          <Box component="span" sx={{ fontSize: '11px', color: '#555', width: 100, flexShrink: 0, lineHeight: '22px' }}>
            {section.label}:
          </Box>
          {section.viewIcons.map((p, i) => (
            <PermIcon key={i} icon={p.icon} tooltip={p.tooltip} bgcolor={p.bgcolor} />
          ))}
          {section.editIcons.length > 0 && (
            <>
              <Box component="span" sx={{ fontSize: '12px', color: '#bbb', lineHeight: '22px', mx: 0.25 }}>/</Box>
              {section.editIcons.map((p, i) => (
                <PermIcon key={i} icon={p.icon} tooltip={p.tooltip} bgcolor={p.bgcolor} />
              ))}
            </>
          )}
        </Box>
      ))}
    </Box>
  );
};

const ProfileManagementPage: React.FC = () => {
  const theme = useTheme();
  const linkBlue = theme.palette.smartSkillTalk?.linkBlue ?? '#1976d2';
  const { currentUser } = useSelector((state: RootState) => state.user);
  const companyId = currentUser?.companyId || '';
  const canCreateNew = canAccess(currentUser, 'profileMgmt.edit.canCreateNew');
  const canEditItem = canAccess(currentUser, 'profileMgmt.edit.canUpdate');
  const canDeleteItem = canAccess(currentUser, 'profileMgmt.edit.canDelete');

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingProfile, setEditingProfile] = useState<Profile | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<Profile | null>(null);
  const [deleteErrorMessage, setDeleteErrorMessage] = useState<string | null>(null);

  const { data, loading, refetch } = useQuery(LIST_PROFILES, {
    variables: { companyId, limit: 100 },
    skip: !companyId,
    fetchPolicy: 'cache-and-network',
  });

  const profiles: Profile[] = useMemo(() => data?.listProfiles?.items || [], [data]);

  const [createProfile, { loading: creating }] = useMutation(CREATE_PROFILE);
  const [updateProfile, { loading: updating }] = useMutation(UPDATE_PROFILE);
  const [deleteProfile, { loading: deleting }] = useMutation(DELETE_PROFILE);

  const saving = creating || updating || deleting;

  const handleOpenCreate = () => {
    setEditingProfile(null);
    setDialogOpen(true);
  };

  const handleOpenEdit = (p: Profile) => {
    setEditingProfile(p);
    setDialogOpen(true);
  };

  const handleClose = () => {
    if (saving) return;
    setDialogOpen(false);
    setEditingProfile(null);
  };

  const handleSubmit = async (values: ProfileFormValues) => {
    const result = editingProfile
      ? await updateProfile({
          variables: {
            input: {
              profileId: editingProfile.profileId,
              name: values.name,
              description: values.description || null,
              permissions: stripTypename(values.permissions),
            },
          },
        })
      : await createProfile({
          variables: {
            input: {
              companyId,
              name: values.name,
              description: values.description || null,
              permissions: stripTypename(values.permissions),
            },
          },
        });
    if (result.errors && result.errors.length > 0) {
      throw new Error(result.errors[0].message);
    }
    setDialogOpen(false);
    setEditingProfile(null);
    await refetch();
  };

  const handleOpenDeleteDialog = (p: Profile) => {
    setDeleteTarget(p);
    setDeleteErrorMessage(null);
    setDeleteDialogOpen(true);
  };

  const handleCloseDeleteDialog = () => {
    if (deleting) return;
    setDeleteErrorMessage(null);
    setDeleteDialogOpen(false);
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    const result = await deleteProfile({ variables: { profileId: deleteTarget.profileId } });
    if (result.errors && result.errors.length > 0) {
      const msg = result.errors[0].message;
      if (msg.includes('still assigned')) {
        setDeleteErrorMessage('割り当てられているユーザーが存在するため削除できません。プロファイルの割り当てを解除してから削除してください。');
      } else {
        setDeleteErrorMessage(msg || 'プロファイルの削除に失敗しました');
      }
      return;
    }
    setDeleteDialogOpen(false);
    await refetch();
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleOpenCreate}
          disabled={!canCreateNew || saving}
          sx={{
            bgcolor: '#5EB600',
            color: 'white',
            borderRadius: '10px',
            '&:hover': { bgcolor: '#5EB600', opacity: 0.9 },
          }}
        >
          新規プロファイル
        </Button>
      </Box>

      {loading ? (
        <Paper sx={{ width: '100%', overflow: 'hidden' }}>
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
            <CircularProgress />
          </Box>
        </Paper>
      ) : (
        <Paper sx={{ width: '100%', overflow: 'hidden' }}>
          <TableContainer>
            <Table aria-label="プロファイル一覧">
              <TableHead sx={{ bgcolor: '#F0F7FF' }}>
                <TableRow>
                  <TableCell align="center" sx={{ color: linkBlue, fontSize: '13px' }}>
                    名称
                  </TableCell>
                  <TableCell align="center" sx={{ color: linkBlue, fontSize: '13px' }}>
                    説明
                  </TableCell>
                  <TableCell align="center" sx={{ color: linkBlue, fontSize: '13px' }}>
                    権限サマリー
                  </TableCell>
                  <TableCell align="center" sx={{ color: linkBlue, fontSize: '13px' }}>
                    更新日
                  </TableCell>
                  <TableCell align="center" sx={{ color: linkBlue, fontSize: '13px' }}>
                    操作
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {profiles.length === 0 ? (
                  <TableRow>
                    <TableCell
                      colSpan={5}
                      align="center"
                      sx={{ color: '#333333', fontSize: '13px', py: 3 }}
                    >
                      プロファイルは登録されていません。
                    </TableCell>
                  </TableRow>
                ) : (
                  profiles.map((p, index) => {
                    const isSystem = p.createdBy === 'system';
                    return (
                    <TableRow
                      key={p.profileId}
                      hover
                      sx={{
                        bgcolor: index % 2 === 0 ? '#FFFFFF' : '#F9F9F9',
                        '&:hover': { bgcolor: '#F5F5F5' },
                      }}
                    >
                      <TableCell
                        align="center"
                        sx={{ color: '#333333', fontSize: '13px', letterSpacing: '0.05em' }}
                      >
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
                          {p.name}
                          {isSystem && (
                            <Chip label="システム" size="small" variant="outlined" sx={{ fontSize: '11px', height: '20px', color: '#888', borderColor: '#ccc' }} />
                          )}
                        </Box>
                      </TableCell>
                      <TableCell
                        align="left"
                        sx={{ color: '#333333', fontSize: '13px', letterSpacing: '0.05em' }}
                      >
                        {p.description || '-'}
                      </TableCell>
                      <TableCell align="left" sx={{ py: 1, whiteSpace: 'nowrap' }}>
                        <PermissionSummary perms={p.permissions} />
                      </TableCell>
                      <TableCell
                        align="center"
                        sx={{ color: '#333333', fontSize: '13px', letterSpacing: '0.05em' }}
                      >
                        {formatDate(p.updatedAt)}
                      </TableCell>
                      <TableCell align="center">
                        <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1 }}>
                          {!isSystem && (
                            <>
                              {canEditItem && (
                                <Tooltip title="編集">
                                  <IconButton
                                    size="small"
                                    onClick={() => handleOpenEdit(p)}
                                    disabled={saving}
                                    sx={{
                                      bgcolor: '#5D84AF',
                                      color: 'white',
                                      '&:hover': { bgcolor: '#5D84AF', opacity: 0.9 },
                                    }}
                                  >
                                    <EditIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                              )}
                              {canDeleteItem && (
                                <Tooltip title="削除">
                                  <IconButton
                                    size="small"
                                    onClick={() => handleOpenDeleteDialog(p)}
                                    disabled={saving}
                                    sx={{
                                      bgcolor: '#F44336',
                                      color: 'white',
                                      '&:hover': { bgcolor: '#F44336', opacity: 0.9 },
                                    }}
                                  >
                                    <DeleteIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                              )}
                            </>
                          )}
                        </Box>
                      </TableCell>
                    </TableRow>
                  );
                  })
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}

      <ProfileFormDialog
        open={dialogOpen}
        initial={editingProfile}
        onClose={handleClose}
        onSubmit={handleSubmit}
        saving={saving}
      />

      <Dialog
        open={deleteDialogOpen}
        onClose={(_, reason) => {
          if (reason !== 'backdropClick' && reason !== 'escapeKeyDown') {
            handleCloseDeleteDialog();
          }
        }}
        PaperProps={{ sx: { borderRadius: '10px' } }}
      >
        <DialogTitle sx={{ bgcolor: '#FF4D4D', color: 'white', fontSize: '18px', fontWeight: 400 }}>
          プロファイルの削除
        </DialogTitle>
        <DialogContent sx={{ mt: 2 }}>
          <DialogContentText>
            「{deleteTarget?.name}」を削除しますか？<br />
            割り当てられているユーザーがいる場合は失敗します。<br />
            この操作は元に戻せません。
          </DialogContentText>
          {deleteErrorMessage && (
            <Alert severity="error" sx={{ mt: 2 }} onClose={() => setDeleteErrorMessage(null)}>
              {deleteErrorMessage}
            </Alert>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button
            onClick={handleDelete}
            variant="contained"
            disabled={deleting}
            startIcon={deleting ? <CircularProgress size={18} color="inherit" /> : undefined}
            sx={{ bgcolor: '#FF4D4D', color: 'white', borderRadius: '6px' }}
          >
            削除
          </Button>
          <Button onClick={handleCloseDeleteDialog} disabled={deleting} variant="outlined">
            キャンセル
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ProfileManagementPage;
